import { useMemo } from 'react'
import MemberPagePopUp from '../memberPagePopUp/MemberPagePopUp'
import {
  ProductivityListItem,
  type ProductivityForEditShape,
  type ProductivityNormalizedServiceType,
  type ProductivityVisitTypeItem,
  type ServiceTypeArrayUpdater,
  type ProductivityForEditUpdater,
} from '../../../components/productivity-list/productivity-list-item/ProductivityListItem'
import { useServiceTypes } from '../../../hooks/useServiceTypes'
import type { NormalizedServiceType } from '../../../utils/normalizeServiceTypes'

type ServiceTypeWithVisitTypes = {
  ServiceTypeId?: string | null
  VisitTypes: ProductivityVisitTypeItem[]
  [key: string]: unknown
}

type Props = {
  onCloseProductivityPopUp: () => void
  productivityForEdit: ProductivityForEditShape | null
  selectedServiceTypeViewModeArray: ServiceTypeWithVisitTypes[]
  setExtraLongWordsToolTip: (tooltip: { top: number; left: number; title: string } | null) => void
  setSelectedServiceTypeArray: (updater: ServiceTypeArrayUpdater) => void
  setProductivityForEdit: (updater: ProductivityForEditUpdater) => void
  onSaveProductivity: (productivity: ProductivityForEditShape) => void
}

const getNormalizedVisitTypes = (
  selectedServiceType: ServiceTypeWithVisitTypes[],
): ProductivityNormalizedServiceType[] =>
  selectedServiceType.map((serviceType) => {
    const normalizedVisitTypes = serviceType.VisitTypes.reduce<ProductivityNormalizedServiceType['VisitTypes']>(
      (visitTypesNorm, visitTypesValue) => {
        if (visitTypesValue.VisitTypeId != null) {
          visitTypesNorm.entities[visitTypesValue.VisitTypeId] = visitTypesValue
        }
        visitTypesNorm.ids.push(visitTypesValue.VisitTypeId)
        return visitTypesNorm
      },
      { entities: {}, ids: [] },
    )
    const { VisitTypes: _vt, ...rest } = serviceType
    return { ...rest, VisitTypes: normalizedVisitTypes }
  })

const ProductivityPopUp = ({
  onCloseProductivityPopUp,
  productivityForEdit,
  selectedServiceTypeViewModeArray,
  setExtraLongWordsToolTip,
  setSelectedServiceTypeArray,
  setProductivityForEdit,
  onSaveProductivity,
}: Props) => {
  const { ServiceTypeId, VisitTypes, serviceValue } = productivityForEdit ?? {}
  const { normalizedServiceTypes } = useServiceTypes()

  const normalizedVisitTypes = useMemo(
    () => getNormalizedVisitTypes(selectedServiceTypeViewModeArray ?? []),
    [selectedServiceTypeViewModeArray],
  )

  const selectedServiceType: NormalizedServiceType | undefined =
    ServiceTypeId ? normalizedServiceTypes.entities[ServiceTypeId] : undefined

  const handleSave = () => {
    if (!productivityForEdit) return
    const isProductivityChanged = productivityForEdit.VisitTypes.find(
      ({ isAdjusted, isNeedToUpdateDuration }) => isAdjusted || isNeedToUpdateDuration,
    )

    if (isProductivityChanged) {
      onSaveProductivity(productivityForEdit)
    } else {
      onCloseProductivityPopUp()
    }
  }

  return (
    <MemberPagePopUp
      isOpen={productivityForEdit !== null}
      title='Edit productivity'
      onClosePopUp={onCloseProductivityPopUp}
      onSave={handleSave}
      content={
        <div className='pb-[250px]'>
          <div className='mb-sp-2 text-2xl font-bold leading-[27.6px] tracking-[0.05em]'>{serviceValue}</div>
          {/* Grid header — matches ProductivityList.scss base columns but with popup-specific proportions */}
          <div className='grid grid-cols-[3.2fr_1.3fr_1fr_1.5fr] grid-rows-[24px] text-lg font-normal leading-6 tracking-[0.05em] text-text-dark'>
            <div className='text-start'>Visit type</div>
            <div className='text-center'>Default length</div>
            <div className='text-center'>Adjust</div>
            <div className='text-center'>Adjusted length</div>
          </div>
          {VisitTypes?.map((visitType) => (
            <ProductivityListItem
              selectedServiceType={selectedServiceType}
              key={visitType.VisitTypeId}
              visitType={visitType}
              normalizedVisitTypes={normalizedVisitTypes}
              setExtraLongWordsToolTip={setExtraLongWordsToolTip}
              setSelectedServiceTypeArray={setSelectedServiceTypeArray}
              setProductivityForEdit={setProductivityForEdit}
              serviceTypeId={ServiceTypeId}
            />
          ))}
        </div>
      }
    />
  )
}

export default ProductivityPopUp
